import { useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, FloppyDisk, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const UNITS = ["page", "document", "batch", "hour"];

export default function MISServices() {
  const { activeId } = useCompany();
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_id: "", name: "", unit: "page", rate: 0 });

  const load = async () => {
    if (!activeId) return;
    const [{ data: cs }, { data: ss }] = await Promise.all([
      api.get("/mis2/clients", { params: { company_id: activeId } }).catch(() => ({ data: [] })),
      api.get("/mis2/services", { params: { company_id: activeId } }),
    ]);
    setClients(cs || []);
    setServices(ss || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const openNew = () => { setEditing(null); setForm({ client_id: "", name: "", unit: "page", rate: 0 }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ client_id: s.client_id, name: s.name, unit: s.unit, rate: s.rate ?? 0 }); setOpen(true); };

  const save = async () => {
    if (!form.client_id || !form.name) { toast.error("Client + name required"); return; }
    try {
      if (editing) {
        await api.put(`/mis2/services/${editing.id}`, { name: form.name, unit: form.unit, rate: Number(form.rate) });
        toast.success("Service updated");
      } else {
        await api.post("/mis2/services", { company_id: activeId, ...form, rate: Number(form.rate) });
        toast.success("Service added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"?`)) return;
    await api.delete(`/mis2/services/${s.id}`);
    setServices((curr) => curr.filter((x) => x.id !== s.id));
    toast.success("Deleted");
  };

  const filtered = clientFilter === "all" ? services : services.filter((s) => s.client_id === clientFilter);

  if (!isAdmin) {
    return <Layout title="MIS Services" subtitle="Admin-only"><div className="text-sm text-[#717680] p-6">Only admin can manage services and rates.</div></Layout>;
  }

  return (
    <Layout
      title="MIS · Services & Rates"
      subtitle={`${services.length} service${services.length === 1 ? "" : "s"} configured across ${clients.length} client${clients.length === 1 ? "" : "s"}`}
      actions={
        <>
          <button onClick={async () => {
            if (!window.confirm("This will scan all MIS entries and fix any broken client/service links by auto-restoring missing clients and re-pointing entries to the correct service. Continue?")) return;
            try {
              const { data } = await api.post("/mis2/services/repair", null, { params: { company_id: activeId } });
              toast.success(`Repair complete · ${data.relinked_entries} entries re-linked · ${data.created_clients} client(s) restored · ${data.created_services} service(s) restored`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
          }} className="inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 border border-[#0D9488] text-[#0D9488] text-xs md:text-sm font-semibold hover:bg-[#0D9488] hover:text-white" data-testid="repair-mis-button">
            <Wrench size={14} weight="bold" /> <span className="hidden sm:inline">Repair Links</span>
          </button>
          <button onClick={openNew} className="inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#D90429] text-white text-xs md:text-sm font-semibold hover:bg-[#EF233C]" data-testid="add-service-button">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Add Service</span><span className="sm:hidden">Add</span>
          </button>
        </>
      }
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-full sm:w-80 rounded-none" data-testid="client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="services-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>
              {["Client", "Service", "Unit", "Rate (₹)", ""].map((h) => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#717680]">No services yet. Click <b>Add Service</b>.</td></tr>}
            {filtered.map((s) => {
              const client = clients.find((c) => c.id === s.client_id);
              return (
                <tr key={s.id} className="border-t border-black/5" data-testid={`service-row-${s.id}`}>
                  <td className="px-4 py-3 font-semibold">{client?.name || "—"}</td>
                  <td className="px-4 py-3">{s.name}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest font-mono-shelv text-[#717680]">{s.unit}</td>
                  <td className="px-4 py-3 font-mono-shelv">₹{Number(s.rate || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-black/5" data-testid={`edit-${s.id}`}><PencilSimple size={14}/></button>
                    <button onClick={() => remove(s)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`del-${s.id}`}><Trash size={14}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>{editing ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))} disabled={!!editing}>
                <SelectTrigger className="rounded-none" data-testid="svc-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Service name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Scanning & Digitization" className="rounded-none" data-testid="svc-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="svc-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Rate per unit (₹)</Label>
                <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} className="rounded-none" data-testid="svc-rate-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]" data-testid="svc-save"><FloppyDisk size={14} weight="bold" className="inline mr-1" />Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
